// PurchasingDashboard.jsx
// Main purchasing hub: reorder reminders + draft POs + nav to vendors/POs

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HTTPService } from '../../../services/httpService';
import PurchasingAPI from '../../../services/purchasingService';
import StatusBadge from './StatusBadge';
import StockStatusBadge, { getStockStatus } from './StockStatusBadge';
import DemandPreviewDrawer from './DemandPreviewDrawer';
import BulkPOCreationPreview from './BulkPOCreationPreview';
import CreatePOModal from './CreatePOModal';
import VendorList from '../vendors/VendorList';
import { isMultiLocation, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import { convertFromUTC } from '../../../util/convert_timezone';
import ScopeService from '../../../services/scopeService';
import { toast } from 'react-toastify';

const API = new HTTPService();
const DESKTOP_BREAKPOINT = 1024;

class PurchasingDashboard extends Component {
    constructor(props) {
        super(props);
        const params = new URLSearchParams(props.location?.search || '');
        const tabParam = params.get('tab');
        this.state = this.getInitialState(tabParam);
    }

    getInitialState = (tabParam) => ({
        activeTab: tabParam === 'vendors' ? 'vendors' : 'purchasing',
        reorderTasks: [],
        purchaseOrders: [],
        poStatusFilter: 'all',
        selectedTasks: new Set(),

        // Demand preview
        showDemandDrawer: false,
        demandProductId: null,
        demandProductName: null,

        // Bulk create
        showBulkPreview: false,

        // Create PO modal
        showCreatePOModal: false,

        loading: true,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,
        error: null
    });

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location?.search !== this.props.location?.search) {
            const params = new URLSearchParams(this.props.location?.search || '');
            const tabParam = params.get('tab');
            const newTab = tabParam === 'vendors' ? 'vendors' : 'purchasing';
            if (newTab !== this.state.activeTab) {
                this.setState({ activeTab: newTab });
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadData = async () => {
        try {
            const taskParams = {
                installerId: this.props.typeId,
                task_type: 'reorder',
                status: 'open,in_progress',
                userId: ScopeService.getUserId(),
                userRole: ScopeService.getUserRole(),
                ...(this.props.selectedLocationId && this.props.selectedLocationId !== 'all'
                    ? { locationId: this.props.selectedLocationId }
                    : {})
            };
            const [tasksRes, allPOs] = await Promise.all([
                API.get('/api/tasks', taskParams).catch(() => ({ data: [] })),
                PurchasingAPI.getPurchaseOrders(this.props.typeId,
                    this.props.selectedLocationId && this.props.selectedLocationId !== 'all'
                        ? { locationId: this.props.selectedLocationId }
                        : {}
                ).catch(() => [])
            ]);

            const rawTasks = tasksRes.data?.data || tasksRes.data || [];
            const tasks = (Array.isArray(rawTasks) ? rawTasks : []).map(t => ({
                ...t,
                task_id: t.task_id || t.id
            }));
            const purchaseOrders = (Array.isArray(allPOs) ? allPOs : []).map(po => ({
                ...po,
                purchase_order_id: po.purchase_order_id || po.id
            }));

            this.setState({
                reorderTasks: tasks,
                purchaseOrders,
                selectedTasks: new Set(),
                loading: false
            });
        } catch (err) {
            console.error('Error loading purchasing dashboard:', err);
            this.setState({ error: 'Failed to load purchasing data.', loading: false });
        }
    };

    handleToggleTask = (taskId) => {
        this.setState(prev => {
            const selected = new Set(prev.selectedTasks);
            if (selected.has(taskId)) {
                selected.delete(taskId);
            } else {
                selected.add(taskId);
            }
            return { selectedTasks: selected };
        });
    };

    handleSelectAll = () => {
        const { reorderTasks, selectedTasks } = this.state;
        if (selectedTasks.size === reorderTasks.length) {
            this.setState({ selectedTasks: new Set() });
        } else {
            this.setState({ selectedTasks: new Set(reorderTasks.map(t => t.task_id)) });
        }
    };

    handleSnooze = async (taskId) => {
        const days = window.prompt('Snooze for how many days?', '7');
        if (!days) return;
        try {
            await API.post(`/api/tasks/${taskId}/snooze`, { days: parseInt(days, 10) });
            toast.info('Reminder snoozed.');
            await this.loadData();
        } catch (err) {
            console.error('Error snoozing task:', err);
            toast.error('Failed to snooze.');
        }
    };

    handleDismiss = async (taskId) => {
        if (!window.confirm('Dismiss this reorder reminder?')) return;
        try {
            await API.post(`/api/tasks/${taskId}/complete`, { resolution_type: 'user_dismissed' });
            toast.info('Reminder dismissed.');
            await this.loadData();
        } catch (err) {
            console.error('Error dismissing task:', err);
        }
    };

    handleViewDemand = (task) => {
        this.setState({
            showDemandDrawer: true,
            demandProductId: task.entity_id,
            demandProductName: task.product_name || task.entity_name || task.title
        });
    };

    handleBulkCreate = () => {
        if (this.state.selectedTasks.size === 0) {
            toast.warning('Select at least one reminder.');
            return;
        }
        this.setState({ showBulkPreview: true });
    };

    handleBulkComplete = async () => {
        this.setState({ showBulkPreview: false });
        await this.loadData();
    };

    getDaysSince = (dateStr) => {
        if (!dateStr) return '—';
        const created = new Date(dateStr);
        const now = new Date();
        return Math.floor((now - created) / (1000 * 60 * 60 * 24));
    };

    getFilteredPOs = () => {
        const { purchaseOrders, poStatusFilter } = this.state;
        if (poStatusFilter === 'all') return purchaseOrders;
        return purchaseOrders.filter(po => po.status === poStatusFilter);
    };

    getPOTabCount = (tab) => {
        const { purchaseOrders } = this.state;
        if (tab === 'all') return purchaseOrders.length;
        return purchaseOrders.filter(po => po.status === tab).length;
    };

    render() {
        const { activeTab, reorderTasks, purchaseOrders, poStatusFilter, selectedTasks, loading, error, isDesktop,
                showDemandDrawer, demandProductId, demandProductName, showBulkPreview, showCreatePOModal } = this.state;
        const { locations } = this.props;
        const selectedLocId = this.props.selectedLocationId;
        const showLocationColumn = isMultiLocation(locations) && (!selectedLocId || selectedLocId === 'all');

        return (
            <div className={`purchasing-container ${!isDesktop ? 'mobile' : ''}`}>
                <style>{`
                    .purchasing-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: var(--space-6);
                        background: var(--color-bg-panel);
                        min-height: 100vh;
                    }
                    .purchasing-container.mobile { padding: var(--space-4); }
                    .priority-badge {
                        display: inline-block;
                        padding: 2px var(--space-2);
                        border-radius: var(--radius-sm);
                        font-size: 10px;
                        font-weight: var(--font-weight-semibold);
                        text-transform: uppercase;
                    }
                    .priority-high { background: var(--color-danger-light); color: var(--color-danger-dark); }
                    .priority-normal { background: var(--color-info-light); color: var(--color-info-dark); }
                    .priority-low { background: var(--color-gray-100); color: var(--color-gray-500); }
                    .po-status-tab .tab-count {
                        margin-left: var(--space-2);
                        font-size: var(--font-size-xs);
                        color: var(--color-text-muted);
                    }
                `}</style>

                {/* Tab Toggle */}
                <div className="text-center mb-3">
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className={`btn ${activeTab === 'purchasing' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.setState({ activeTab: 'purchasing' })}>
                            Purchasing
                        </button>
                        <button
                            type="button"
                            className={`btn ${activeTab === 'vendors' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.setState({ activeTab: 'vendors' })}>
                            Vendors
                        </button>
                    </div>
                </div>

                {activeTab === 'vendors' ? (
                    <VendorList
                        userType={this.props.userType}
                        typeId={this.props.typeId}
                        user={this.props.user}
                        locations={locations}
                        selectedLocationId={this.props.selectedLocationId}
                        embedded={true}
                    />
                ) : loading ? (
                    <div className="text-center py-5">
                        <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-3">Loading purchasing data...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                ) : (
                    <>
                        {/* Needs Reorder */}
                        <div className="jv-card">
                            <div className="jv-card-header">
                                <h5 className="jv-card-title">
                                    <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" style={{ color: 'var(--color-warning)' }}></i>
                                    Needs Reorder ({reorderTasks.length})
                                </h5>
                                {reorderTasks.length > 0 && (
                                    <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={this.handleBulkCreate}
                                        disabled={selectedTasks.size === 0}>
                                        <i className="fa-sharp fa-regular fa-file-plus"></i>
                                        Create Draft POs ({selectedTasks.size})
                                    </button>
                                )}
                            </div>

                            {reorderTasks.length === 0 ? (
                                <div className="jv-empty-state">
                                    <i className="fa-sharp fa-regular fa-check-circle" style={{ color: 'var(--color-success)' }}></i>
                                    <p>No reorder reminders. Inventory levels are healthy.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="jv-table jv-table-sm">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input type="checkbox"
                                                        checked={selectedTasks.size === reorderTasks.length && reorderTasks.length > 0}
                                                        onChange={this.handleSelectAll} />
                                                </th>
                                                <th>Product</th>
                                                {showLocationColumn && <th>Location</th>}
                                                <th>On Hand</th>
                                                <th>Incoming</th>
                                                <th>Committed</th>
                                                <th>Projected</th>
                                                <th>Priority</th>
                                                <th>Age</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reorderTasks.map(task => {
                                                const projected = (task.on_hand || 0) + (task.incoming || 0) - (task.committed || 0);
                                                const priorityClass = task.priority === 'high' ? 'priority-high' :
                                                    task.priority === 'low' ? 'priority-low' : 'priority-normal';
                                                return (
                                                    <tr key={task.task_id}>
                                                        <td>
                                                            <input type="checkbox"
                                                                checked={selectedTasks.has(task.task_id)}
                                                                onChange={() => this.handleToggleTask(task.task_id)} />
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 'var(--font-weight-medium)' }}>{task.product_name || task.entity_name || task.title}</div>
                                                            {task.brand && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{task.brand}</div>}
                                                        </td>
                                                        {showLocationColumn && (
                                                            <td>
                                                                {task.location_id ? (
                                                                    <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(task.location_id, locations) }}>
                                                                        {getLocationName((locations || []).find(l => l.location_id === task.location_id) || {})}
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                        )}
                                                        <td>{task.on_hand ?? '—'}</td>
                                                        <td style={{ color: 'var(--color-info)' }}>{task.incoming ?? '—'}</td>
                                                        <td style={{ color: 'var(--color-danger)' }}>{task.committed ?? '—'}</td>
                                                        <td>
                                                            <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>{projected}</span>
                                                            <span style={{ marginLeft: '6px' }}>
                                                                <StockStatusBadge status={getStockStatus(projected, task.reorder_point || 0)} />
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`priority-badge ${priorityClass}`}>
                                                                {task.priority || 'Normal'}
                                                            </span>
                                                        </td>
                                                        <td>{this.getDaysSince(task.created_at)}d</td>
                                                        <td>
                                                            <button className="jv-btn-icon" onClick={() => this.handleViewDemand(task)} title="View Demand">
                                                                <i className="fa-sharp fa-regular fa-chart-line"></i>
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.handleSnooze(task.task_id)} title="Snooze">
                                                                <i className="fa-sharp fa-regular fa-clock"></i>
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.handleDismiss(task.task_id)} title="Dismiss">
                                                                <i className="fa-sharp fa-regular fa-xmark"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Purchase Orders */}
                        <div className="jv-card">
                            <div className="jv-card-header">
                                <h5 className="jv-card-title">
                                    <i className="fa-sharp fa-regular fa-file-invoice mr-2"></i>
                                    Purchase Orders ({purchaseOrders.length})
                                </h5>
                                <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={() => this.setState({ showCreatePOModal: true })}>
                                    <i className="fa-sharp fa-regular fa-plus"></i> New PO
                                </button>
                            </div>

                            <div className="jv-filter-bar">
                                {['all', 'draft', 'submitted', 'partial', 'received', 'cancelled'].map(tab => (
                                    <button
                                        key={tab}
                                        className={`jv-filter-tab ${poStatusFilter === tab ? 'active' : ''}`}
                                        onClick={() => this.setState({ poStatusFilter: tab })}
                                        style={{ textTransform: 'capitalize' }}
                                    >
                                        {tab}
                                        <span className="tab-count">({this.getPOTabCount(tab)})</span>
                                    </button>
                                ))}
                            </div>

                            {this.getFilteredPOs().length === 0 ? (
                                <div className="jv-empty-state">
                                    <i className="fa-sharp fa-regular fa-file-invoice"></i>
                                    <p>No purchase orders{poStatusFilter !== 'all' ? ` with status "${poStatusFilter}"` : ''}.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="jv-table">
                                        <thead>
                                            <tr>
                                                <th>PO #</th>
                                                <th>Vendor</th>
                                                {showLocationColumn && <th>Location</th>}
                                                <th>Status</th>
                                                <th>Lines</th>
                                                <th>Total</th>
                                                {isDesktop && <th>Expected</th>}
                                                {isDesktop && <th>Created</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.getFilteredPOs().map(po => (
                                                <tr key={po.purchase_order_id}>
                                                    <td>
                                                        <Link to={`/purchase-orders/${po.purchase_order_id}`} style={{ color: 'var(--color-brand-teal)', textDecoration: 'none', fontWeight: 'var(--font-weight-medium)' }}>
                                                            {po.po_number || `PO-${po.purchase_order_id}`}
                                                        </Link>
                                                    </td>
                                                    <td>{po.vendor_name || '—'}</td>
                                                    {showLocationColumn && (
                                                        <td>
                                                            {po.location_id ? (
                                                                <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(po.location_id, locations) }}>
                                                                    {getLocationName((locations || []).find(l => l.location_id === po.location_id) || {})}
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                    )}
                                                    <td><StatusBadge status={po.status} /></td>
                                                    <td>{po.line_count || 0}</td>
                                                    <td>{po.total_cost ? `$${parseFloat(po.total_cost).toFixed(2)}` : '—'}</td>
                                                    {isDesktop && <td>{po.expected_date ? convertFromUTC(po.expected_date).format('MM/DD/YY') : '—'}</td>}
                                                    {isDesktop && <td>{po.created_at ? convertFromUTC(po.created_at).format('MM/DD/YY') : '—'}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Demand Preview Drawer */}
                {showDemandDrawer && (
                    <DemandPreviewDrawer
                        show={showDemandDrawer}
                        onHide={() => this.setState({ showDemandDrawer: false })}
                        productId={demandProductId}
                        productName={demandProductName}
                        installerId={this.props.typeId}
                        locationId={this.props.selectedLocationId}
                    />
                )}

                {/* Bulk PO Preview */}
                {showBulkPreview && (
                    <BulkPOCreationPreview
                        show={showBulkPreview}
                        onHide={() => this.setState({ showBulkPreview: false })}
                        tasks={reorderTasks.filter(t => selectedTasks.has(t.task_id))}
                        installerId={this.props.typeId}
                        onComplete={this.handleBulkComplete}
                    />
                )}

                {/* Create PO Modal */}
                {showCreatePOModal && (
                    <CreatePOModal
                        show={showCreatePOModal}
                        onHide={() => this.setState({ showCreatePOModal: false })}
                        installerId={this.props.typeId}
                        locations={locations}
                        selectedLocationId={this.props.selectedLocationId}
                        onCreated={(poId) => {
                            this.setState({ showCreatePOModal: false });
                            this.props.history.push(`/purchase-orders/${poId}`);
                        }}
                    />
                )}
            </div>
        );
    }
}

export default PurchasingDashboard;
